/**
 * Cloudflare Service Adapter
 * 
 * This module provides an adapter that lets the application work with a Cloudflare-hosted backend.
 * It handles API calls to the Cloudflare Workers endpoints.
 */

// Import the centralized API configuration
import { API_BASE } from '../../shared/utils/apiConfig';
import i18n from '../../i18n';

const ca = (key, opts) => i18n.t(`serviceMessages.cloudAdapter.${key}`, opts);
const cm = (key, opts) => i18n.t(`serviceMessages.common.${key}`, opts);

// Session token management
const getToken = () => localStorage.getItem('qawaem_token');
const getHeaders = () => {
  const token = getToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
  };
};

// Generic API call helper
const apiCall = async (endpoint, method = 'GET', data = null) => {
  try {
    const url = `${API_BASE}${endpoint}`;
    console.log(`📡 API Call: ${method} ${url}`);
    
    if (data) {
      console.log('📡 API Call Data:', JSON.stringify(data, null, 2));
    }
    
    const options = {
      method,
      headers: getHeaders(),
      credentials: API_BASE.includes('localhost') ? 'include' : 'same-origin', // Only use include for localhost
      mode: 'cors'
    };

    if (data && (method === 'POST' || method === 'PUT' || method === 'PATCH')) {
      options.body = JSON.stringify(data);
    }

    console.log('📡 API Options:', JSON.stringify({
      method: options.method,
      headers: options.headers,
      bodyLength: options.body ? options.body.length : 0
    }, null, 2));

    const response = await fetch(url, options);
    console.log(`📡 API Response status: ${response.status}`);

    if (response.status === 401) {
      // Handle unauthorized (e.g., expired token)
      try { 
        localStorage.removeItem('qawaem_token'); 
        localStorage.removeItem('qawaem_user'); 
      } catch (error) {
        console.error('Error removing auth data from localStorage:', error);
      }
      
      // Use hash-router path (skip login gate in Qawaem direct-access mode)
      if (typeof window !== 'undefined' && window.location.hash !== '#/setup') {
        const skipAuth = localStorage.getItem('qawaem_license');
        try {
          const lic = skipAuth ? JSON.parse(skipAuth) : null;
          if (lic?.key === 'QAWAEM-DIRECT') {
            return null;
          }
        } catch (_) { /* noop */ }
      }
      window.location.href = '/#/setup';
      throw new Error(ca('auth_expired'));
    }

    if (!response.ok) {
      let errorMessage = cm('api_error', { status: response.status });
      let errorDetails = null;
      
      try {
        const errorData = await response.json();
        errorDetails = errorData;
        errorMessage = errorData.error || errorData.message || errorMessage;
        console.error(`📡 API Error Response:`, errorData);
      } catch (e) {
        // If we can't parse the error JSON, just use the status code message
        console.error('Failed to parse error response:', e);
        try {
          const errorText = await response.text();
          console.error('📡 API Error Response Text:', errorText);
        } catch (textError) {
          console.error('Could not get error response text:', textError);
        }
      }
      
      console.error(`📡 Full API Error Details:`, {
        url,
        method,
        status: response.status,
        statusText: response.statusText,
        errorMessage,
        errorDetails,
        requestData: data
      });
      
      throw new Error(errorMessage);
    }

    // Return null for 204 No Content
    if (response.status === 204) {
      return null;
    }

    // Parse the JSON response
    let result;
    try {
      result = await response.json();
      console.log('API Response data:', result);
    } catch (error) {
      console.error('Error parsing JSON response:', error);
      throw new Error(ca('invalid_response'));
    }
    
    // Automatically unwrap { success, data } pattern
    if (result && typeof result === 'object' && 'data' in result) {
      return result.data;
    }
    return result;
  } catch (error) {
    console.error(`API Call Error for ${endpoint}:`, error);
    throw error;
  }
};



// Disable localStorage fallback; always use backend
const shouldUseLocalStorageFallback = () => false;

const localStorageService = {
  getAll: () => {
    const stored = localStorage.getItem('qawaem_clients');
    return Promise.resolve(stored ? JSON.parse(stored) : []);
  },
  
  getById: (id) => {
    const stored = JSON.parse(localStorage.getItem('qawaem_clients') || '[]');
    return Promise.resolve(stored.find(client => client.id === id) || null);
  },
  
  createClient: (clientData) => {
    const stored = JSON.parse(localStorage.getItem('qawaem_clients') || '[]');
    const newClient = {
      ...clientData,
      id: Date.now().toString(),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    stored.push(newClient);
    localStorage.setItem('qawaem_clients', JSON.stringify(stored));
    return Promise.resolve(newClient);
  },
  
  updateClient: (id, updates) => {
    const stored = JSON.parse(localStorage.getItem('qawaem_clients') || '[]');
    const index = stored.findIndex(client => client.id === id);
    if (index !== -1) {
      stored[index] = { ...stored[index], ...updates, updated_at: new Date().toISOString() };
      localStorage.setItem('qawaem_clients', JSON.stringify(stored));
      return Promise.resolve(stored[index]);
    }
    return Promise.reject(new Error('Client not found'));
  },
  
  deleteClient: (id) => {
    const stored = JSON.parse(localStorage.getItem('qawaem_clients') || '[]');
    const filtered = stored.filter(client => client.id !== id);
    localStorage.setItem('qawaem_clients', JSON.stringify(filtered));
    return Promise.resolve(true);
  }
};

// Client services
export const clientService = shouldUseLocalStorageFallback() ? {
  // Use localStorage for local development
  getAll: localStorageService.getAll,
  getClients: localStorageService.getAll,
  getById: localStorageService.getById,
  getClient: localStorageService.getById,
  createClient: localStorageService.createClient,
  updateClient: localStorageService.updateClient,
  deleteClient: localStorageService.deleteClient
} : {
  // Use API for production
  getAll: (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      queryParams.append(key, value);
    });
    const queryString = queryParams.toString();
    return apiCall(`/clients${queryString ? '?' + queryString : ''}`);
  },
  
  getClients: (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      queryParams.append(key, value);
    });
    const queryString = queryParams.toString();
    return apiCall(`/clients${queryString ? '?' + queryString : ''}`);
  },
  
  getById: (id) => apiCall(`/clients/${id}`),
  getClient: (id) => apiCall(`/clients/${id}`),
  
  createClient: (clientData) => apiCall('/clients', 'POST', clientData),
  
  updateClient: (id, updates) => apiCall(`/clients/${id}`, 'PUT', updates),
  
  deleteClient: (id) => apiCall(`/clients/${id}`, 'DELETE')
};

// Invoice services
export const invoiceService = {
  getInvoices: (params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    return apiCall(`/invoices${queryParams ? '?' + queryParams : ''}`);
  },
  
  getInvoice: (id) => apiCall(`/invoices/${id}`),
  
  createInvoice: (invoiceData) => apiCall('/invoices', 'POST', invoiceData),
  
  updateInvoice: (id, updates) => apiCall(`/invoices/${id}`, 'PUT', updates),
  
  deleteInvoice: (id) => apiCall(`/invoices/${id}`, 'DELETE'),
  
  getInvoicePdf: (id) => {
    window.open(`${API_BASE}/invoices/${id}/pdf`, '_blank');
  },
  
  // Real-time collaborative editing using Durable Objects
  joinInvoiceSession: (invoiceId, userId, userName) => {
    return apiCall(`/live/invoice/${invoiceId}`, 'POST', { userId, userName });
  },
  
  updateInvoiceContent: (invoiceId, userId, content) => {
    return apiCall(`/live/invoice/${invoiceId}/update`, 'POST', { userId, content });
  },
  
  leaveInvoiceSession: (invoiceId, userId) => {
    return apiCall(`/live/invoice/${invoiceId}/leave`, 'DELETE', { userId });
  }
};

// Product services
export const productService = {
  getProducts: async (params = {}) => {
    try {
      console.log('Getting products with params:', params);
      const queryParams = new URLSearchParams(params).toString();
      const result = await apiCall(`/products${queryParams ? '?' + queryParams : ''}`);
      console.log('Got products result:', result);
      return result;
    } catch (error) {
      console.error('Error getting products:', error);
      throw error;
    }
  },
  
  getProduct: async (id) => {
    try {
      console.log('Getting product:', id);
      const result = await apiCall(`/products/${id}`);
      console.log('Got product result:', result);
      return result;
    } catch (error) {
      console.error(`Error getting product ${id}:`, error);
      throw error;
    }
  },
  
  createProduct: async (productData) => {
    try {
      console.log('Creating product with data:', productData);
      const result = await apiCall('/products', 'POST', productData);
      console.log('Created product result:', result);
      return result;
    } catch (error) {
      console.error('Error creating product:', error);
      throw error;
    }
  },
  
  updateProduct: async (id, updates) => {
    try {
      console.log('Updating product', id, 'with data:', updates);
      
      // Ensure types are correct
      const apiUpdates = { ...updates };
      
      // Handle status conversion if needed
      if (apiUpdates.status === 'active' && apiUpdates.active === undefined) {
        apiUpdates.active = 1;
      } else if (apiUpdates.status === 'inactive' && apiUpdates.active === undefined) {
        apiUpdates.active = 0;
      }
      
      // Handle number values
      if (typeof apiUpdates.price === 'string') {
        apiUpdates.price = parseFloat(apiUpdates.price) || 0;
      }
      
      if (typeof apiUpdates.stock === 'string' && apiUpdates.stock !== null) {
        apiUpdates.stock = parseInt(apiUpdates.stock) || 0;
      }
      
      const result = await apiCall(`/products/${id}`, 'PUT', apiUpdates);
      console.log('Updated product result:', result);
      return result;
    } catch (error) {
      console.error(`Error updating product ${id}:`, error);
      throw error;
    }
  },
  
  deleteProduct: async (id) => {
    try {
      console.log('Deleting product:', id);
      const result = await apiCall(`/products/${id}`, 'DELETE');
      console.log('Deleted product result:', result);
      return result;
    } catch (error) {
      console.error(`Error deleting product ${id}:`, error);
      throw error;
    }
  }
};

// Settings service
export const settingsService = {
  // Get settings from the server
  getSettings: () => apiCall('/settings'),
  
  // Update full settings object
  updateSettings: (updates) => apiCall('/settings', 'PUT', updates)
};

// Dashboard service
export const dashboardService = {
  getStats: () => apiCall('/dash/stats'),
  
  refreshStats: () => apiCall('/dash/stats/refresh', 'POST')
};

// Employee services
export const employeeService = {
  getEmployees: (params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    return apiCall(`/hr/employees${queryParams ? '?' + queryParams : ''}`);
  },
  
  getEmployee: (id) => apiCall(`/hr/employees/${id}`),
  
  createEmployee: (employeeData) => apiCall('/hr/employees', 'POST', employeeData),
  
  updateEmployee: (id, updates) => apiCall(`/hr/employees/${id}`, 'PUT', updates),
  
  deleteEmployee: (id) => apiCall(`/hr/employees/${id}`, 'DELETE')
};

// Category services
export const categoryService = {
  getCategories: (params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    return apiCall(`/categories${queryParams ? '?' + queryParams : ''}`);
  },
  
  getCategory: (id) => apiCall(`/categories/${id}`),
  
  createCategory: (categoryData) => apiCall('/categories', 'POST', categoryData),
  
  updateCategory: (id, updates) => apiCall(`/categories/${id}`, 'PUT', updates),
  
  deleteCategory: (id) => apiCall(`/categories/${id}`, 'DELETE')
};

// Partner services
// Always use /partners endpoint for Cloudflare Workers API
const partnerBasePath = () => '/partners';

export const partnerService = {
  getPartners: (params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    console.log(`Fetching partners with params:`, params);
    return apiCall(`${partnerBasePath()}${queryParams ? '?' + queryParams : ''}`);
  },
  getPartner: (id) => apiCall(`${partnerBasePath()}/${id}`),
  createPartner: (partnerData) => {
    console.log('Creating partner with data:', partnerData);
    return apiCall(partnerBasePath(), 'POST', partnerData);
  },
  updatePartner: (id, updates) => apiCall(`${partnerBasePath()}/${id}`, 'PUT', updates),
  deletePartner: (id) => apiCall(`${partnerBasePath()}/${id}`, 'DELETE')
};

// Document services
export const documentService = {
  getDocuments: (params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    return apiCall(`/documents${queryParams ? '?' + queryParams : ''}`);
  },
  
  getDocument: (id) => apiCall(`/documents/${id}`),
  
  createDocument: (documentData) => apiCall('/documents', 'POST', documentData),
  
  updateDocument: (id, updates) => apiCall(`/documents/${id}`, 'PUT', updates),
  
  deleteDocument: (id) => apiCall(`/documents/${id}`, 'DELETE')
};

export default {
  clients: clientService,
  invoices: invoiceService,
  products: productService,
  settings: settingsService,
  dashboard: dashboardService,
  employees: employeeService,
  categories: categoryService,
  partners: partnerService,
  documents: documentService
};

